import { useRouterState } from "@tanstack/react-router";
import { Languages } from "lucide-react";
import { languageSwitchTarget } from "@/lib/language-switch";

interface LanguageSwitcherProps {
  className?: string;
  showIcon?: boolean;
  onNavigate?: () => void;
  tabIndex?: number;
  ariaHidden?: boolean;
}

/**
 * Links to the same page in the other language, falling back to the language
 * home page when that page has no translation yet. It only navigates — no
 * preference is stored and no automatic redirect ever happens.
 */
export function LanguageSwitcher({
  className,
  showIcon = true,
  onNavigate,
  tabIndex,
  ariaHidden,
}: LanguageSwitcherProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const target = languageSwitchTarget(pathname);

  return (
    <a
      href={target.href}
      hrefLang={target.lang}
      lang={target.lang}
      onClick={() => {
        onNavigate?.();
      }}
      tabIndex={tabIndex}
      aria-hidden={ariaHidden}
      aria-label={
        target.lang === "es"
          ? "Ver esta página en español"
          : "View this page in English"
      }
      className={className}
    >
      {showIcon ? (
        <Languages size={14} aria-hidden="true" className="inline-block mr-1 align-[-2px]" />
      ) : null}
      {target.label}
    </a>
  );
}
